import useSWR from 'swr';
import { useMemo } from 'react';
import {
  fetchLoyaltyModuleHealth,
  type LoyaltyModuleHealth,
} from '../gateway/loyaltyAdmin.gateway.js';
import {
  isLoyaltyOperational,
  resolveLoyaltyOperationalState,
  type LoyaltyOperationalState,
} from '../utils/loyaltyAvailability.js';
import { adminSwrTenantScope } from '../../../shared/swr/adminSwrTenantKey.js';

export interface UseLoyaltyModuleHealthResult {
  readonly health: LoyaltyModuleHealth | undefined;
  readonly operationalState: LoyaltyOperationalState | 'loading';
  readonly isOperational: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refresh: () => Promise<unknown>;
}

export function getLoyaltyModuleHealthSwrKey(routeTenantCode?: string | null): readonly ['loyalty-module-health', string] {
  return ['loyalty-module-health', adminSwrTenantScope(routeTenantCode)] as const;
}

export function useLoyaltyModuleHealth(
  enabled: boolean,
  routeTenantCode?: string | null,
): UseLoyaltyModuleHealthResult {
  const swrKey = enabled ? getLoyaltyModuleHealthSwrKey(routeTenantCode) : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    fetchLoyaltyModuleHealth,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      // Must fetch on first mount when key becomes non-null — false+false left
      // hermetic e2e (and cold SPA boots) stuck on operationalState=loading forever.
      revalidateOnMount: true,
      revalidateIfStale: true,
      dedupingInterval: 30_000,
    },
  );

  const operationalState = useMemo(
    () => resolveLoyaltyOperationalState(data),
    [data],
  );

  const isOperational = useMemo(
    () => (data !== undefined ? isLoyaltyOperational(data) : false),
    [data],
  );

  return {
    health: data,
    operationalState,
    isOperational,
    isLoading,
    isError: error !== undefined,
    refresh: mutate,
  };
}
